import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { isPanelRequired } from '../api/client';
import { useApi } from '../api/context';
import type { MerchantPayment, PaymentReceivedEvent, QrIntentUpdatedEvent } from '../api/types';
import { DateRangeForm } from '../components/DateRangeForm';
import { useToast } from '../components/Toasts';
import { ErrorNotice, Loading, PageHeader, PaymentStateChip, StatCard, Upsell } from '../components/ui';
import { useI18n } from '../i18n';
import { formatDateTime, formatTime, skopjeToday } from '../lib/dates';
import { formatDen } from '../lib/money';
import { presetRange, toApiRange, type DayRangeValue, type PeriodPreset } from '../lib/ranges';
import { useDebounced, useLoad } from '../lib/useLoad';
import { useLiveEvents } from '../live/live';
import { usePanelActive } from '../state/merchant';

const PRESETS: readonly PeriodPreset[] = ['today', 'yesterday', 'week', 'month'];
const LATEST_COUNT = 10;

interface Paying {
  intentId: string;
  payerName: string | null;
  amountMinor: number | null;
}

/** Today's (or the period's) sales at a glance, updated live as customers pay. */
export function OverviewPage() {
  const { t, locale } = useI18n();
  const api = useApi();
  const notify = useToast();
  const panelActive = usePanelActive();
  const today = skopjeToday();
  const [range, setRange] = useState<DayRangeValue>(() => presetRange('today', today));
  const [paying, setPaying] = useState<Paying | null>(null);

  const { state, reload } = useLoad(
    async (signal) => {
      const apiRange = toApiRange(range);
      const [summary, payments] = await Promise.all([
        api.summary(apiRange, signal),
        api.payments(apiRange, undefined, signal),
      ]);
      return { summary, payments, at: new Date().toISOString() };
    },
    [api, range.from, range.to],
  );

  const refresh = useDebounced(() => reload({ quiet: true }), 250);

  useLiveEvents(['payment.received', 'qr_intent.updated', 'resync'], (type, data) => {
    if (type === 'qr_intent.updated') {
      const e = data as QrIntentUpdatedEvent;
      if (e.inProgress) setPaying({ intentId: e.intentId, payerName: e.payerName, amountMinor: e.amountMinor });
      else setPaying((p) => (p && p.intentId === e.intentId ? null : p));
    } else if (type === 'payment.received') {
      const e = data as PaymentReceivedEvent;
      if (e.kind !== 'MERCHANT') return;
      setPaying(null);
      notify(t('overview.newPayment', { amount: formatDen(e.amountMinor), payer: e.counterpartyName }));
    }
    refresh();
  });

  let body: ReactNode;
  if (state.status === 'loading') body = <Loading />;
  else if (state.status === 'error') {
    body = isPanelRequired(state.error) ? (
      <Upsell reason="history" />
    ) : (
      <ErrorNotice error={state.error} onRetry={() => reload()} />
    );
  } else {
    const { summary, payments, at } = state.data;
    const latest = payments.items.slice(0, LATEST_COUNT);
    body = (
      <>
        <div className="stats" aria-live="polite">
          <StatCard label={t('overview.count')} value={summary.count} testId="stat-count" />
          <StatCard label={t('overview.gross')} value={formatDen(summary.grossMinor)} testId="stat-gross" />
          <StatCard label={t('overview.fee')} value={formatDen(summary.mdrMinor)} testId="stat-fee" />
          <StatCard
            label={t('overview.net')}
            value={formatDen(summary.netMinor)}
            tone="accent"
            testId="stat-net"
          />
        </div>
        <p className="muted small">
          {t('overview.settledOnly')} {t('overview.feeNote')}
        </p>

        <section className="card" aria-labelledby="latest-title">
          <div className="card-header">
            <h2 id="latest-title">{t('overview.latest')}</h2>
            <div className="card-header-side">
              <span className="muted small">{t('app.updated', { time: formatTime(at) })}</span>
              <Link to="/payments" className="btn btn--link">
                {t('overview.seeAll')}
              </Link>
            </div>
          </div>
          {latest.length === 0 ? (
            <p className="empty">{t('overview.empty')}</p>
          ) : (
            <ul className="feed" data-testid="latest-payments">
              {latest.map((p) => (
                <LatestRow key={p.paymentId} payment={p} locale={locale} />
              ))}
            </ul>
          )}
        </section>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('overview.title')} />
      <DateRangeForm
        value={range}
        today={today}
        maxDays={92}
        presets={PRESETS}
        panelActive={panelActive}
        onChange={setRange}
      />
      {paying ? (
        <div className="notice notice--info paying" role="status" data-testid="paying">
          <span className="pulse" aria-hidden="true" />
          {paying.payerName
            ? t('overview.paying', {
                payer: paying.payerName,
                amount: paying.amountMinor !== null ? formatDen(paying.amountMinor) : '',
              })
            : t('overview.payingAnon', {
                amount: paying.amountMinor !== null ? formatDen(paying.amountMinor) : '',
              })}
        </div>
      ) : null}
      {body}
    </>
  );
}

function LatestRow({ payment: p, locale }: { payment: MerchantPayment; locale: 'mk' | 'en' }) {
  const { t } = useI18n();
  return (
    <li className="feed-row" data-reference={p.reference} data-state={p.state}>
      <div className="feed-main">
        <span className="feed-payer">{p.payerName}</span>
        <span className="feed-meta">
          {formatDateTime(p.settledAt ?? p.createdAt, locale)} · {t(`payments.kind.${p.kind}`)} ·{' '}
          <span className="mono">{p.reference}</span>
        </span>
      </div>
      <div className="feed-side">
        <span className="feed-amount">{formatDen(p.amountMinor)}</span>
        <PaymentStateChip state={p.state} />
      </div>
    </li>
  );
}
